export type SessionErrorKind = 'unauthenticated' | 'unavailable' | 'request';

const GATEWAY_STATUSES = new Set([502, 503, 504, 521, 522, 523, 525, 526]);

export function classifySessionError(error: unknown): SessionErrorKind {
  const status = getHttpStatus(error);

  if (status === 401 && !isHtmlResponse(error)) {
    return 'unauthenticated';
  }

  if (status == null || GATEWAY_STATUSES.has(status) || isHtmlResponse(error)) {
    return 'unavailable';
  }

  return 'request';
}

export function shouldClearSessionOnError(error: unknown): boolean {
  return classifySessionError(error) === 'unauthenticated';
}

export function isBackendUnreachable(error: unknown): boolean {
  return classifySessionError(error) === 'unavailable';
}

function getHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;

  const record = error as {
    statusCode?: unknown;
    status?: unknown;
    response?: { status?: unknown };
  };

  if (typeof record.statusCode === 'number') return record.statusCode;
  if (typeof record.response?.status === 'number') return record.response.status;
  if (typeof record.status === 'number') return record.status;

  return undefined;
}

function isHtmlResponse(error: unknown): boolean {
  const headers = getResponseHeaders(error);
  const contentType = getHeader(headers, 'content-type');
  return typeof contentType === 'string' && contentType.toLowerCase().includes('text/html');
}

function getResponseHeaders(error: unknown): unknown {
  if (!error || typeof error !== 'object') return undefined;
  const response = (error as { response?: { headers?: unknown } }).response;
  return response?.headers;
}

function getHeader(headers: unknown, name: string): string | undefined {
  if (!headers || typeof headers !== 'object') return undefined;

  const record = headers as Record<string, unknown> & {
    get?: (key: string) => unknown;
  };
  const direct = record[name] ?? record[name.toLowerCase()];
  if (typeof direct === 'string') return direct;

  if (typeof record.get === 'function') {
    const value = record.get(name);
    if (typeof value === 'string') return value;
  }

  return undefined;
}
